import { he } from "date-fns/locale"
import { DictType } from "@pma/types"
import { SyntheticEvent, useState } from "react"
import { useMutation } from "@tanstack/react-query"
import { toast } from "@hooks/use-toast"
import { Card, CardContent, CardDescription, CardFooter } from "@components/common/card"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import { Avatar, AvatarFallback, AvatarImage } from "@components/common/avatar"
import { formatDistance } from "date-fns"
import NotificationActions from "./notification-actions"
import { useDrawer } from "@stores/drawer-store"
import { cn, formatTime } from "@pma/utils/helpers"
import { useAuth } from "@stores/auth-store"
import useTranslator from "@hooks/use-translator"
import { PROJECTS_PAGE_ROUTE } from "@pma/utils/page-routes"
import { useNavigate } from "react-router-dom"
import { markMessageAsRead } from "@pma/utils/utils"
import { Popover, PopoverContent, PopoverTrigger } from "../common/popover"
import { Separator } from "@components/common/separator"
import { Badge } from "@components/common/badge"

interface Props {
  notification: DictType
  context?: "all" | "unread" | "mentioned" | "assigned" | "logs"
  onNotificationClick: (notification: any) => void
}
const NotificationCard = ({ notification, context, onNotificationClick }: Props) => {
  const { translate, locale } = useTranslator()
  const [showOptions, setShowOptions] = useState(true)
  const [isAssignUserList, setIsAssignUserList] = useState<Boolean>(false)
  const drawer = useDrawer()

  const { setTabManager } = useAuth()
  const navigate = useNavigate()

  const isHebrew = locale === "he"

  const taskId = notification?.singe_action?.task_id || notification?.changes?.task_id
  const parentTaskId = notification?.singe_action?._tasks?.parent_tasks_id
  const projectId = notification?.singe_action?._projects?.id

  const handleNotificationClick = () => {
    if (notification._is_read === false) {
      notificationClickMutation.mutate(notification.id)
    }

    // Navigate to financial module for bill notifications
    if (notification?.singe_action?.bill_id && notification?.singe_action?.bill_id !== 0) {
      const billProjectId = notification?.singe_action?.project_id || notification?.singe_action?._projects?.id || projectId
      navigate(PROJECTS_PAGE_ROUTE + `/${billProjectId}?main=${notification?.singe_action?.boq_id || ''}`)
      return
    }

    // Open tasks sidebar for all other notification types
    if (taskId || parentTaskId) {
      context !== "logs" && drawer.onOpen()
      if (drawer.context === "updates") {
        drawer.setFromUpdates(true)
      } else {
        drawer.setFromUpdates(false)
      }
      drawer.setContext("notes")
      drawer.setTaskId(taskId)
      setTabManager({ group: "project-detail", main: "tasks", sub: "groups" })

      navigate(PROJECTS_PAGE_ROUTE + `/${projectId}?task=${parentTaskId || taskId}&showBtn=`)
    } else {
      drawer.onClose()
      projectId && navigate(PROJECTS_PAGE_ROUTE + `/${projectId}`)
    }
  }

  const notificationClickMutation = useMutation({
    mutationKey: ["read-unread-notification"],
    mutationFn: (id: number) => markMessageAsRead([id]),
    onError: (error) => {
      toast({
        title: translate("Error"),
        description: translate("Error occurred while changing message status"),
        variant: "error",
      })
    },
  })

  const handleAssignList = (e: SyntheticEvent) => {
    e.stopPropagation()
    setIsAssignUserList(!isAssignUserList)
  }

  const getAssignUserList = (arr: any) => {
    if (arr.length >= 3) {
      return (
        <Popover>
          <div className='flex items-center'>
            {arr?.slice(0, 2).map((user: any, index: number) => (
              <Tooltip key={index}>
                <TooltipTrigger>
                  <Avatar className={cn("relative h-5 w-5", isHebrew ? "-mr-2" : "-ml-2")}>
                    <AvatarImage
                      src={user?.user_picture?.url || `${process.env.PUBLIC_URL}/user-profile.svg`}
                    />
                  </Avatar>
                </TooltipTrigger>
                <TooltipContent>
                  <h4> {user?.name}</h4>
                </TooltipContent>
              </Tooltip>
            ))}
            {arr?.length > 2 && (
              <PopoverTrigger onClick={handleAssignList}>
                <div
                  className={cn(
                    "relative h-5 w-5 rounded-full bg-blue-100",
                    isHebrew ? "-mr-2" : "-ml-2",
                  )}
                >
                  +{arr.length - 2}
                </div>
              </PopoverTrigger>
            )}
          </div>

          <PopoverContent className='max-h-[350px] max-w-[250px] overflow-y-auto'>
            <>
              {arr?.length > 0 && isAssignUserList && (
                <>
                  <div className='flex w-full flex-wrap gap-2'>
                    {arr?.map((user: any, index: number) => (
                      <Badge
                        className='relative rounded-full bg-slate-500/60 text-white'
                        variant={"outline"}
                        key={index}
                      >
                        {user.name}
                      </Badge>
                    ))}
                  </div>
                  <Separator className='my-2' />
                </>
              )}
              {arr?.map((user: DictType, index: number) => (
                <Tooltip key={index} delayDuration={0}>
                  <TooltipTrigger asChild>
                    <div className={"flex flex-row gap-2"}>
                      <Avatar className={cn("h-7 w-7", isHebrew ? "-mr-2.5" : "-ml-2.5")}>
                        <AvatarImage
                          src={
                            user.user_picture
                              ? user.user_picture.url
                              : `${process.env.PUBLIC_URL}/usr.svg`
                          }
                          alt='Avatar'
                        />
                        <AvatarFallback className='uppercase'>{user?.name}</AvatarFallback>
                      </Avatar>
                      <span className=''>{user.name}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side='bottom' className='flex items-center gap-4'>
                    {user.name}
                  </TooltipContent>
                </Tooltip>
              ))}
            </>
          </PopoverContent>
        </Popover>
      )
    } else if (arr.length <= 2) {
      return arr?.map((user: DictType, index: number) => (
        <Tooltip key={index} delayDuration={0}>
          <TooltipTrigger asChild>
            <div className={""}>
              <Avatar className={cn("h-7 w-7", isHebrew ? "-mr-2.5" : "-ml-2.5")}>
                <AvatarImage
                  src={
                    user.user_picture ? user.user_picture.url : `${process.env.PUBLIC_URL}/usr.svg`
                  }
                  alt='Avatar'
                />
                <AvatarFallback className='uppercase'>
                  {user?.first_name && user?.first_name[0]}
                  {user?.last_name && user?.last_name[0]}
                </AvatarFallback>
              </Avatar>
              <span className='sr-only'>{user.name}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent side='bottom' className='flex items-center gap-4'>
            {user.name}
          </TooltipContent>
        </Tooltip>
      ))
    }
  }

  const UserAvatar = ({ notification }: { notification: any }) => {
    return (
      <div className='hidden md:flex'>
        <img
          src={
            notification?.singe_action?._by_user?.user_picture?.url ||
            notification?.changes?._by_user?.user_picture?.url ||
            `${process.env.PUBLIC_URL}/user-profile.svg`
          }
          alt={notification?.singe_action?._by_user?.name}
          aria-hidden='true'
          className={cn("h-8 w-8 rounded-full", isHebrew ? "ml-1" : "mr-1")}
        />
      </div>
    )
  }

  return (
    <Card
      onMouseEnter={() => setShowOptions(true)}
      onMouseLeave={() => setShowOptions(false)}
      className={`relative flex h-max flex-col justify-center p-0 ${notification?._is_read ? "shadow" : "bg-primary/10"}`}
    >
      <CardContent
        className='flex h-full overflow-y-hidden p-2'
        onClick={() => handleNotificationClick()}
      >
        <UserAvatar notification={notification} />
        <CardDescription className='flex w-full flex-1 flex-col'>
          <div className='flex w-full flex-col'>
            <div className='flex flex-col gap-2'>
              <div className='flex flex-wrap items-center gap-1 text-xs'>
                {notification?.singe_action?.bill_id != 0 ? null : notification?.singe_action
                    ?.action_id ? (
                  <span className='font-semibold'>
                    {notification?.singe_action?._by_user?.name}{" "}
                  </span>
                ) : (
                  <span className='font-semibold'> {notification?.changes?._by_user?.name} </span>
                )}
                <span className='flex items-center gap-2 font-semibold text-primary'>
                  {notification?.singe_action?.action_id ? (
                    <div>
                      {isHebrew
                        ? notification.singe_action?._action?.he ||
                          translate(notification.singe_action?._action?.en)
                        : notification.singe_action?._action?.en}
                    </div>
                  ) : (
                    <div>
                      {isHebrew
                        ? notification.changes?._change_type?.he ||
                          translate(notification.changes?._change_type?.en)
                        : notification?.changes?._change_type?.en}
                    </div>
                  )}
                </span>
                <span className=''>
                  {notification?.singe_action?.bill_id != 0 ? (
                    <span className='font-bold'>
                      {translate("by")} {notification?.singe_action?._by_user?.name}
                    </span>
                  ) : notification?.singe_action?.action_id ? (
                    <span className='font-bold'>
                      {notification.singe_action?._action?.en === "assigned you"
                        ? translate("a")
                        : translate("in")}{" "}
                      {translate("task")}
                    </span>
                  ) : null}
                </span>
                {notification?.singe_action._action?.en && (
                  <div className='flex gap-1'>
                    <Tooltip>
                      <TooltipTrigger>
                        <p className='max-w-[25ch] truncate text-ellipsis font-semibold text-primary'>
                          {notification?.singe_action?._tasks?.title}
                        </p>
                      </TooltipTrigger>
                      <TooltipContent className='bg-white text-black'>
                        <p className=''>{notification?.singe_action?._tasks?.title}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                )}
                {notification?.singe_action?._projects?.id ? (
                  <>
                    <span>{translate("in") + " " + translate("project")}</span>
                    <Tooltip>
                      <TooltipTrigger>
                        <p className='max-w-[25ch] truncate text-ellipsis font-semibold text-primary'>
                          {notification?.singe_action?._projects.title}{" "}
                        </p>
                      </TooltipTrigger>
                      <TooltipContent className='bg-white text-black'>
                        <span className=''>{notification?.singe_action?._projects.title}</span>
                      </TooltipContent>
                    </Tooltip>
                  </>
                ) : null}{" "}
                {notification?.changes.changes?.change_type?.en === "new task" && (
                  <div className='flex gap-1'>
                    <span className='font-bold'>{translate("named")}</span>
                    <p className='max-w-[25ch] truncate text-ellipsis font-semibold text-primary'>
                      {notification?.changes?._task?.title}
                    </p>
                  </div>
                )}
                <div className='flex flex-1 items-center justify-center'>
                  {notification?.singe_action?.action_id ? (
                    <div
                      className={`flex w-full justify-between truncate text-ellipsis text-xs ${notification?.singe_action?._task?.title ? "inline" : "hidden"}`}
                    >
                      <span className='max-w-[25ch] flex-1 truncate text-ellipsis'>
                        <span className='font-bold'> {translate("to")}</span>&nbsp;{" "}
                        {notification?.changes._task?.title}
                      </span>{" "}
                      &nbsp;{" "}
                      <strong className='text-[9px] text-muted-foreground'>
                        <span className='font-bold'> {translate("Task")}</span>
                      </strong>
                    </div>
                  ) : (
                    <div
                      className={`flex w-full justify-between truncate text-ellipsis text-xs ${notification?.singe_action?._task?.description ? "inline" : "hidden"}`}
                    >
                      <span className='flex-1'>
                        &nbsp; {notification?.singe_action?._task?.description}
                      </span>{" "}
                      &nbsp;
                    </div>
                  )}
                </div>
                <div className={`flex items-end justify-between pr-[50x] text-slate-500`}>
                  <p>
                    <small>
                      {notification?.created_at &&
                        formatDistance(notification?.created_at, new Date(), {
                          locale: isHebrew ? he : undefined,
                          addSuffix: true,
                        })}
                    </small>{" "}
                    <small> @ {formatTime(notification?.created_at, { amPm: true })} </small>{" "}
                  </p>
                </div>
              </div>
            </div>
            <div className='flex cursor-pointer items-center justify-end truncate text-ellipsis pe-3'>
              {getAssignUserList(notification?.recepients)}
            </div>
          </div>
        </CardDescription>
      </CardContent>
      <CardFooter className='m-0 py-0 pt-5'>
        {showOptions && (
          <NotificationActions
            setShowOptions={setShowOptions}
            notification={notification}
            revalidate={onNotificationClick}
            likes={notification?.like_by}
            isRead={notification._is_read}
          />
        )}
      </CardFooter>
    </Card>
  )
}
export default NotificationCard
